import type { ShareTextTemplateType } from "@/lib/types";
import { formatPrice } from "@/lib/utils";

/**
 * Default share text templates
 */
export const DEFAULT_SHARE_TEMPLATES = {
  option_a: `*{Item Title}*
*{Price}* | *{Location}* 
View more here or send a DM to buy: {item link}`,

  option_b: `*{Item Title}* for sale on Sell It Off!

* Price: *{ItemPrice}*
* Location: *{ItemLocation}*
* Description: *{ItemDescription}*
* Defects: *{Defects}*

View it here or send a DM here: {ItemLink}`,
};

/**
 * Available placeholders for share text templates
 */
export const SHARE_TEMPLATE_PLACEHOLDERS = {
  "{Item Title}": "The item title",
  "{ItemTitle}": "The item title",
  "{Price}": "Formatted price (e.g., ₦10,000)",
  "{ItemPrice}": "Formatted price (e.g., ₦10,000)",
  "{Location}": "Item location",
  "{ItemLocation}": "Item location",
  "{Description}": "Item description (omitted if empty)",
  "{ItemDescription}": "Item description (omitted if empty)",
  "{Defects}": "Item defects (omitted if empty)",
  "{item link}": "Full URL to the item page",
  "{ItemLink}": "Full URL to the item page",
  "{ItemUrl}": "Full URL to the item page",
};

export interface ShareTemplateData {
  title: string;
  price: number;
  location?: string;
  description?: string;
  defects?: string;
  url: string;
}

export type ShareLocationMode = "full" | "short";

/** Optional saved bodies for preset share templates (trimmed non-empty overrides defaults). */
export type ShareTextPresetOverrides = {
  option_a?: string;
  option_b?: string;
};

export type ShareTemplateRenderOptions = {
  locationMode?: ShareLocationMode;
};

function formatLocationByMode(
  location: string | undefined,
  mode: ShareLocationMode = "full",
): string {
  const raw = location?.trim();
  if (!raw) return "";
  if (mode === "full") {
    return raw;
  }
  const parts = raw
    .split(",")
    .map((p) => p.trim())
    .filter((p) => p.length > 0);
  if (parts.length <= 1) return raw;
  if (parts.length === 2) return `${parts[0]}, ${parts[1]}`;
  const neighborhood = parts[0];
  const state = parts[parts.length - 2];
  return `${neighborhood}, ${state}`;
}

/**
 * Replaces placeholders in a template string with actual values
 */
export function applyShareTemplate(
  template: string,
  data: ShareTemplateData,
  options?: ShareTemplateRenderOptions,
): string {
  let result = template;

  // Replace basic placeholders
  result = result.replace(/\{Item Title\}/g, data.title || "");
  result = result.replace(/\{ItemTitle\}/g, data.title || "");

  const formattedPrice = formatPrice(data.price);
  result = result.replace(/\{Price\}/g, formattedPrice);
  result = result.replace(/\{ItemPrice\}/g, formattedPrice);

  const formattedLocation = formatLocationByMode(
    data.location,
    options?.locationMode ?? "full",
  );
  result = result.replace(/\{Location\}/g, formattedLocation);
  result = result.replace(/\{ItemLocation\}/g, formattedLocation);

  result = result.replace(/\{item link\}/g, data.url);
  result = result.replace(/\{ItemLink\}/g, data.url);
  result = result.replace(/\{ItemUrl\}/g, data.url);

  // Handle conditional fields (description and defects)
  // Remove entire line if the field is empty
  const hasDescription = data.description && data.description.trim().length > 0;
  const hasDefects = data.defects && data.defects.trim().length > 0;

  if (!hasDescription) {
    // Remove lines containing Description placeholder (including preceding newlines and following content until next line)
    result = result.replace(/\n?\*?\s*\*?\{Description\}.*?\n/g, "");
    result = result.replace(/\n?\*?\s*\*?\{ItemDescription\}.*?\n/g, "");
  } else if (data.description) {
    result = result.replace(/\{Description\}/g, data.description);
    result = result.replace(/\{ItemDescription\}/g, data.description);
  }

  if (!hasDefects) {
    // Remove lines containing Defects placeholder (including preceding newlines and following content until next line)
    result = result.replace(/\n?\*?\s*\*?\{Defects\}.*?\n/g, "");
  } else if (data.defects) {
    // Format defects nicely - if it says "No defects", we might want to omit the line
    const defectsText = data.defects.trim().toLowerCase();
    if (defectsText === "no defects" || defectsText === "none") {
      result = result.replace(/\n?\*?\s*\*?\{Defects\}.*?\n/g, "");
    } else {
      result = result.replace(/\{Defects\}/g, data.defects);
    }
  }

  // Clean up multiple consecutive newlines
  result = result.replace(/\n{3,}/g, "\n\n");

  // Trim leading/trailing whitespace
  result = result.trim();

  return result;
}

/**
 * Gets the template text based on the template type
 */
export function getShareTemplateText(
  type: ShareTextTemplateType,
  customTemplate?: string,
  presetOverrides?: ShareTextPresetOverrides,
): string {
  if (type === "custom" && customTemplate) {
    return customTemplate;
  }
  if (type === "option_a") {
    const override = presetOverrides?.option_a?.trim();
    return override && override.length > 0
      ? override
      : DEFAULT_SHARE_TEMPLATES.option_a;
  }
  if (type === "option_b") {
    const override = presetOverrides?.option_b?.trim();
    return override && override.length > 0
      ? override
      : DEFAULT_SHARE_TEMPLATES.option_b;
  }
  return DEFAULT_SHARE_TEMPLATES.option_a;
}

/**
 * Generates share text using the configured template
 */
export function generateShareText(
  type: ShareTextTemplateType,
  data: ShareTemplateData,
  customTemplate?: string,
  presetOverrides?: ShareTextPresetOverrides,
  renderOptions?: ShareTemplateRenderOptions,
): string {
  const templateText = getShareTemplateText(
    type,
    customTemplate,
    presetOverrides,
  );
  return applyShareTemplate(templateText, data, renderOptions);
}
